import { find, flatten, get, uniqBy } from "lodash";
import {
  DOCUMENT_STEPS,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
  INSURANCE_PLAN_PARAMS_RULES,
} from "utils/constants";

const isPlainObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * Collects InsurancePlanParams from attachments' linked insurance plans
 * (used so upsell / surcomplément DOCUMENT_STEPS flags are available).
 */
export const collectParamsFromAttachments = (attachments = []) =>
  uniqBy(
    flatten(
      (attachments || []).map((attachment) => {
        const insurancePlans = get(attachment, "insurancePlans") || [];
        return flatten(insurancePlans.map((plan) => get(plan, "params") || []));
      }),
    ),
    "id",
  );

/**
 * Concatenates base-plan params with params from linked plans on attachments.
 */
export const buildDocumentStepsParamsList = ({
  baseParams = [],
  attachments = [],
}) => [...baseParams, ...collectParamsFromAttachments(attachments)];

/** Defaults for static plan attachments when no DOCUMENT_STEPS override exists. */
export const DEFAULT_DOCUMENT_STEP_FLAGS = {
  [DOCUMENT_STEPS.DEVIS]: true,
  [DOCUMENT_STEPS.CONTRAT]: true,
  [DOCUMENT_STEPS.SIGNATURE]: false,
};

const STATIC_ATTACHMENT_TYPES = new Set(
  Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES),
);

/**
 * Merges the DOCUMENT_STEPS rule value across every InsurancePlanParams entry
 * provided (attachment ids are globally unique, so configs from the base plan
 * and upsell plans can be safely combined into one lookup by attachment id).
 *
 * Accepts `insurancePlan.params` (`[{ rules }]`) or a concatenated list from
 * several plans. Supports rules as an array (API) or keyed object (edit form).
 */
export const getDocumentStepsConfig = (paramsList = []) => {
  const list = Array.isArray(paramsList) ? paramsList : [];
  return list.reduce((acc, params) => {
    const rules = get(params, "rules", []);
    const rule = Array.isArray(rules)
      ? find(rules, { name: INSURANCE_PLAN_PARAMS_RULES.DOCUMENT_STEPS })
      : get(rules, INSURANCE_PLAN_PARAMS_RULES.DOCUMENT_STEPS);
    const value = get(rule, "value");
    if (isPlainObject(value)) {
      return { ...acc, ...value };
    }
    return acc;
  }, {});
};

/**
 * Per-document override:
 * - configured attachment → use the explicit step flag (fallback to defaults)
 * - unconfigured static plan attachment → DEVIS/CONTRAT on, SIGNATURE off
 * - unconfigured dynamic document → preserved (visible)
 */
export const isAttachmentVisibleAtStep = ({
  attachment,
  stepsConfig,
  step,
}) => {
  const config = get(stepsConfig, get(attachment, "id"));
  if (!config) {
    if (!STATIC_ATTACHMENT_TYPES.has(get(attachment, "type"))) {
      return true;
    }
    return get(DEFAULT_DOCUMENT_STEP_FLAGS, step) === true;
  }
  const flag = get(config, step);
  if (flag === undefined) {
    return get(DEFAULT_DOCUMENT_STEP_FLAGS, step) === true;
  }
  return flag === true;
};

/**
 * Filters a list of attachments for a given journey step according to the
 * DOCUMENT_STEPS rules.
 *
 * Always merges `params` / `baseParams` with params linked on each attachment
 * so upsell / surcomplément admin step flags are applied when present.
 */
export const filterAttachmentsByStep = ({
  attachments = [],
  params,
  baseParams,
  step,
}) => {
  const paramsList = buildDocumentStepsParamsList({
    baseParams: params != null ? params : baseParams || [],
    attachments,
  });
  const stepsConfig = getDocumentStepsConfig(paramsList);
  return attachments.filter((attachment) =>
    isAttachmentVisibleAtStep({ attachment, stepsConfig, step }),
  );
};

const attachmentRecency = (attachment) => {
  const createdDate = get(attachment, "createdDate");
  if (createdDate) {
    const timestamp = new Date(createdDate).getTime();
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }
  }
  return get(attachment, "id", "");
};

/**
 * When several attachments share the same name and type, keep only the most
 * recent one (by createdDate, falling back to id).
 */
export const keepLatestAttachmentsByNameAndType = (attachments = []) => {
  const latestByKey = new Map();

  attachments.forEach((attachment) => {
    const name = get(attachment, "name", "");
    const type = get(attachment, "type", "");
    const key = `${name}::${type}`;
    const current = latestByKey.get(key);
    if (
      !current ||
      attachmentRecency(attachment) > attachmentRecency(current)
    ) {
      latestByKey.set(key, attachment);
    }
  });

  return Array.from(latestByKey.values());
};
